import { useTranslation } from "react-i18next";
import { useMergeAudit } from "~/api/queries";
import { Dialog } from "~/foundation/components/Dialog";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatDateTime } from "~/foundation/utils/time";

/**
 * W9 §A.2.2 M10 — new in slate. kms's `MergeStore.audit` method exists and no component calls
 * it; this is the one panel: who decided what, and how many decisions had no attributable
 * contributor.
 */
export function MergeAuditDialog({
  sessionId,
  onClose,
}: {
  sessionId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: audit, isLoading } = useMergeAudit(sessionId);

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Merge audit")}
      closeLabel={t("Close")}
      size="md"
    >
      {isLoading || !audit ? (
        <div className="flex justify-center py-6">
          <Spinner label={t("Loading")} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {audit.unattributed > 0 ? (
            <Text variant="small">
              {t("{{count}} decisions have no attributable contributor.", {
                count: audit.unattributed,
              })}
            </Text>
          ) : null}
          <ul className="flex flex-col gap-1.5">
            {audit.resolutions.map((resolution) => (
              <li key={resolution.partId} className="flex items-center justify-between text-small">
                <span className="text-text">{resolution.partId}</span>
                <span className="text-text-secondary">{resolution.decision}</span>
                <span className="text-text-tertiary">
                  {formatDateTime(resolution.resolvedAt, i18n.language)}
                </span>
              </li>
            ))}
          </ul>
          {audit.resolutions.length === 0 ? (
            <Text variant="small">{t("No decisions have been recorded yet.")}</Text>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
